import { useEffect, useState } from "react";
import { Activity, Users, Mail, Send, Megaphone, CheckCircle2, Clock, Zap } from "lucide-react";
import api from "@/lib/api";

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="brut-card p-5">
    <span className={`grid place-items-center w-11 h-11 rounded-xl border-2 border-ink ${color} mb-3`}><Icon className="w-5 h-5 text-white" /></span>
    <div className="font-display font-bold text-3xl">{value}</div>
    <div className="text-sm text-ink/60">{label}</div>
  </div>
);

const logColor = { lead: "bg-sky", email: "bg-bubble", social: "bg-grape", ai: "bg-sun", system: "bg-slate-300" };

export default function AutomationTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/automation/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  if (!data) return <div className="font-display text-xl">Loading automation status…</div>;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div data-testid="admin-automation">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Automation Status</h1>
          <p className="text-ink/60">Live view of your marketing engine.</p>
        </div>
        <span className={`tag-pill ${data.scheduler_running ? "bg-green-200" : "bg-tangerine/40"}`} data-testid="scheduler-status">
          <Zap className="w-4 h-4" /> Engine {data.scheduler_running ? "running" : "stopped"}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Total leads" value={data.leads_total} color="bg-sky" />
        <Stat icon={Activity} label="Active in sequences" value={data.active_enrollments} color="bg-bubble" />
        <Stat icon={Mail} label="Emails delivered" value={data.emails_sent} color="bg-grape" />
        <Stat icon={CheckCircle2} label="Sequences completed" value={data.completed_enrollments} color="bg-tangerine" />
        <Stat icon={Send} label="Posts scheduled" value={data.posts.scheduled} color="bg-sky" />
        <Stat icon={Megaphone} label="Posts published" value={data.posts.posted} color="bg-bubble" />
        <Stat icon={Activity} label="Ad campaigns" value={data.ad_campaigns} color="bg-grape" />
        <Stat icon={Activity} label="Ad variants" value={data.ad_variants} color="bg-tangerine" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="brut-card p-5">
          <div className="text-sm text-ink/60 mb-1">Active welcome sequence</div>
          <div className="font-display font-bold text-lg">{data.active_sequence || "None active"}</div>
        </div>
        <div className="brut-card p-5">
          <div className="text-sm text-ink/60 mb-1 flex items-center gap-1"><Clock className="w-4 h-4" /> Next email fires</div>
          <div className="font-display font-bold text-lg">{fmt(data.next_email_at)}</div>
        </div>
        <div className="brut-card p-5">
          <div className="text-sm text-ink/60 mb-1 flex items-center gap-1"><Clock className="w-4 h-4" /> Next post publishes</div>
          <div className="font-display font-bold text-lg">{fmt(data.next_post_at)}</div>
        </div>
      </div>

      <div className="brut-card p-5">
        <div className="font-display font-bold text-xl mb-3">Activity feed</div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="automation-logs">
          {data.logs.length === 0 && <div className="text-ink/60">No activity yet.</div>}
          {data.logs.map((l) => (
            <div key={l.id} className="flex items-start gap-3 text-sm border-b border-dashed border-ink/15 pb-2">
              <span className={`tag-pill ${logColor[l.kind] || "bg-white"} !py-0.5 capitalize shrink-0`}>{l.kind}</span>
              <span className="flex-1">{l.message}</span>
              <span className="text-ink/40 text-xs shrink-0">{new Date(l.created_at).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
